// Maestro setup carried on a session (session.maestro) and applied by the
// Maestro component in AeDrive / Copilot when the session is opened.
// The settings inside `locked` / `initial` are configured in the database and
// served by GET /v1/session-fields (BB_SessionService sessionFieldConfig) —
// each field is { fieldName, group, fieldType, defaultValue, label, flavours, ... }.
// `flowPreview` sets the session's preview source; absent = the operator's own.

export const MAESTRO_FLAVOURS = ['aedrive', 'copilot'];
export const supportsMaestro = (flavour) => MAESTRO_FLAVOURS.includes(flavour);

export const MAESTRO_GROUPS = ['locked', 'initial'];

// Preview source choices; '' sends no flowPreview, so operators keep their own.
export const PREVIEW_SOURCE_OPTIONS = [
    { value: '', label: 'Operator’s own setting' },
    { value: 'aero', label: 'Aero' },
    { value: 'whep', label: 'Flow WHEP' },
];

// Built-in copy of the session service's default fields (DEFAULT_SESSION_FIELDS),
// used until the configured list loads, or if it can't be fetched.
export const FALLBACK_MAESTRO_FIELDS = [
    { fieldName: 'skipUnchangedBakeOnIn', group: 'locked', fieldType: 'boolean', defaultValue: false, label: 'Skip Bake on In when nothing changed' },
    { fieldName: 'includeEmptyStrings', group: 'locked', fieldType: 'boolean', defaultValue: false, label: 'Send cleared fields as empty' },
    { fieldName: 'hideDataSourceFields', group: 'locked', fieldType: 'boolean', defaultValue: true, label: 'Hide data-bound fields' },
    { fieldName: 'hideCaptionDrivenFields', group: 'locked', fieldType: 'boolean', defaultValue: false, label: 'Hide caption-set fields' },
    { fieldName: 'ignoreHides', group: 'locked', fieldType: 'boolean', defaultValue: false, label: 'Show every field, ignore the definition’s hides' },
    { fieldName: 'showPreview', group: 'initial', fieldType: 'boolean', defaultValue: true, label: 'Show preview' },
    { fieldName: 'showForm', group: 'initial', fieldType: 'boolean', defaultValue: false, label: 'Show form' },
    { fieldName: 'showJsonViewer', group: 'initial', fieldType: 'boolean', defaultValue: false, label: 'JSON viewer' },
    { fieldName: 'showValuesWindow', group: 'initial', fieldType: 'boolean', defaultValue: false, label: 'Values viewer' },
    { fieldName: 'previewLatched', group: 'initial', fieldType: 'boolean', defaultValue: true, label: 'Hide preview with the window', flavours: ['copilot'] },
].map((field) => ({ enabled: true, required: false, ...field }));

// Fields for one app — a field without `flavours` applies to every app.
export const fieldsForFlavour = (fields, flavour) => fields.filter((field) => (
    !field.flavours?.length || field.flavours.includes(flavour)
));

// Fields arrive in display order; keep it.
export const fieldsInGroup = (fields, group) => fields.filter((field) => field.group === group);

// Form values: booleans as booleans, numbers and strings as the text in their
// input ('' = no value).
const toFormValue = (field, value) => {
    if (field.fieldType === 'boolean') return Boolean(value);
    return value === null || value === undefined ? '' : String(value);
};

// A field's current form value, falling back to its default for fields the
// form was built without (e.g. config loaded after the form opened).
export const getFieldFormValue = (form, field) => {
    const value = form?.[field.group]?.[field.fieldName];
    return value === undefined ? toFormValue(field, field.defaultValue) : value;
};

const fromFormValue = (field, value) => {
    if (field.fieldType === 'boolean') return Boolean(value);
    const text = String(value ?? '').trim();
    if (!text) return null;
    return field.fieldType === 'number' ? Number(text) : text;
};

// Aero URL of a renderer's preview renderer, or '' when it has none.
export const previewRendererUrlFor = (renderer) => (renderer?.dependent_renderer_id
    ? `https://${process.env.REACT_APP_BROKER_URL}/?renderer=${renderer.dependent_renderer_id}`
    : '');

// Editable form state from a session (or a blank one). `enabled` drives the
// "Maestro setup" toggle. A session with no Maestro setup yet starts from the
// renderer's own preview renderer.
export const maestroFormFromSession = (session, suggestedPreviewUrl = '', fields = FALLBACK_MAESTRO_FIELDS) => {
    const maestro = session?.maestro;
    const groupForm = (group) => Object.fromEntries(fieldsInGroup(fields, group).map((field) => {
        const stored = maestro?.[group];
        const value = stored && Object.prototype.hasOwnProperty.call(stored, field.fieldName)
            ? stored[field.fieldName]
            : field.defaultValue;
        return [field.fieldName, toFormValue(field, value)];
    }));
    const flowPreview = maestro?.flowPreview;
    return {
        enabled: Boolean(maestro),
        previewRendererUrl: (maestro ? maestro.previewRendererUrl : suggestedPreviewUrl) || '',
        locked: groupForm('locked'),
        initial: groupForm('initial'),
        flowPreview: {
            source: flowPreview?.source || '',
            endpoint: flowPreview?.endpoint || '',
            rendererId: flowPreview?.rendererId || '',
            apiKey: flowPreview?.apiKey || '',
        },
    };
};

// API `flowPreview`: undefined for the operator's own setting; the endpoint,
// renderer destination and API key are only sent for Flow WHEP.
const toFlowPreviewPayload = (flowPreview) => {
    if (!flowPreview?.source) return undefined;
    const whep = flowPreview.source === 'whep';
    return {
        source: flowPreview.source,
        endpoint: whep ? flowPreview.endpoint.trim() : null,
        rendererId: whep ? flowPreview.rendererId.trim() : null,
        apiKey: whep ? flowPreview.apiKey.trim() : null,
    };
};

// When the renderer changes, follow its preview renderer — unless the user
// typed their own preview URL.
export const retargetPreviewUrl = (form, previousSuggestion, nextSuggestion) => (
    !form.previewRendererUrl || form.previewRendererUrl === previousSuggestion
        ? { ...form, previewRendererUrl: nextSuggestion || '' }
        : form
);

// API payload: null when the toggle is off, so operators keep their own
// saved settings. Only the configured (enabled) fields are sent; the session
// service keeps values stored for fields that have since been disabled.
export const toMaestroPayload = (form, fields = FALLBACK_MAESTRO_FIELDS) => {
    if (!form.enabled) return null;
    const groupPayload = (group) => Object.fromEntries(fieldsInGroup(fields, group)
        .map((field) => [field.fieldName, fromFormValue(field, getFieldFormValue(form, field))]));
    const flowPreview = toFlowPreviewPayload(form.flowPreview);
    return {
        previewRendererUrl: form.previewRendererUrl.trim() || null,
        locked: groupPayload('locked'),
        initial: groupPayload('initial'),
        ...(flowPreview && { flowPreview }),
    };
};

export const isSameMaestro = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// The session's stored setup in payload form, for change detection.
export const sessionMaestroPayload = (session, fields = FALLBACK_MAESTRO_FIELDS) => (session?.maestro
    ? toMaestroPayload(maestroFormFromSession(session, '', fields), fields)
    : null);

const validateField = (field, value) => {
    if (field.fieldType === 'boolean') return null;
    const text = String(value ?? '').trim();
    if (!text) return field.required ? 'Required' : null;
    if (field.fieldType === 'number' && !Number.isFinite(Number(text))) return 'Must be a number';
    return null;
};

const isHttpUrl = (text) => {
    try {
        return ['http:', 'https:'].includes(new URL(text).protocol);
    } catch {
        return false;
    }
};

// Errors keyed like the form:
// { previewRendererUrl, fields: { [fieldName]: msg }, flowPreview: { endpoint, rendererId, apiKey } }.
export const validateMaestroForm = (form, fields = FALLBACK_MAESTRO_FIELDS) => {
    const errors = {};
    if (!form.enabled) return errors;
    const url = form.previewRendererUrl.trim();
    if (url && !isHttpUrl(url)) errors.previewRendererUrl = 'Must be an http(s) URL';
    if (form.flowPreview?.source === 'whep') {
        const { endpoint, rendererId, apiKey } = form.flowPreview;
        const flowErrors = {};
        if (!endpoint.trim()) flowErrors.endpoint = 'Required';
        else if (!isHttpUrl(endpoint.trim())) flowErrors.endpoint = 'Must be an http(s) URL';
        if (!rendererId.trim()) flowErrors.rendererId = 'Required';
        if (!apiKey.trim()) flowErrors.apiKey = 'Required';
        if (Object.keys(flowErrors).length) errors.flowPreview = flowErrors;
    }
    const fieldErrors = Object.fromEntries(fields
        .map((field) => [field.fieldName, validateField(field, getFieldFormValue(form, field))])
        .filter(([, message]) => message));
    if (Object.keys(fieldErrors).length) errors.fields = fieldErrors;
    return errors;
};

// Display label of a stored preview source.
export const previewSourceLabel = (source) => (
    PREVIEW_SOURCE_OPTIONS.find((option) => option.value === (source || ''))?.label || source
);

// Display text for a stored value on the session details page.
export const formatFieldValue = (field, value) => {
    if (field.fieldType === 'boolean') return value ? 'On' : 'Off';
    return value === null || value === undefined || value === '' ? '—' : String(value);
};
